import {Injectable, OnDestroy} from '@angular/core';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {Permission, PermissionAssignment, PermissionObjectType, PermissionSubjectType} from 'smag-commons/dto/api';
import {SmagApiService} from '../api/smag-api.service';
import {map} from 'rxjs/operators';
import {LoginService} from './login.service';

/**
 * Service that provides permission management functionalities.
 */
@Injectable()
export class PermissionsService implements OnDestroy {

    private destroyed = new Subject<void>();
    private overallPermissionsSubj: ReplaySubject<Permission[]>;

    constructor(private readonly apiService: SmagApiService,
                private readonly loginService: LoginService) {
        this.overallPermissionsSubj = new ReplaySubject<Permission[]>(1); // like BehaviorSubject but with no need for initial value
        // reload is triggered every time a user gets logged-in (different principals may have different permissions)
        loginService.addSetupHook(1, profile => {
            // NOTE: hierarchy is actually not needed here, but we do not want to wait for completion
            // of network loading before setting up permissions
            console.debug(`Retrieving overall permissions for user ${profile?.username}`);
            return this.reloadOverallPermissions();
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get overallPermissions(): Observable<Permission[]> {
        return this.overallPermissionsSubj.asObservable();
    }

    getPermissionsForSubject(type: PermissionSubjectType, id?: string): Observable<PermissionAssignment[]> {
        return this.apiService.getPermissionAssignments({subjectType: type, subjectId: id}, null).pipe(
            map(res => res.results)
        );
    }

    getPermissionsForObject(type: PermissionObjectType, id?: string): Observable<PermissionAssignment[]> {
        return this.apiService.getPermissionAssignments(null, {objectType: type, objectId: id}).pipe(
            map(res => res.results)
        );
    }

    assignPermissions(assignment: PermissionAssignment): Promise<void> {
        return this.apiService.updatePermissionAssignment(assignment);
    }

    removePermissions(subjectType: PermissionSubjectType, subjectId: string, objectType: PermissionObjectType, objectId: string): Promise<void> {
        return this.apiService.deletePermissionAssignment(subjectType, subjectId, objectType, objectId);
    }

    reloadOverallPermissions(): Promise<Permission[]> {
        return this.apiService.getOverallPermissions().toPromise()
            .then(perms => {
                this.overallPermissionsSubj.next(perms.permissions);
                return perms.permissions;
            });
    }
}
